<script setup lang="ts">
import { BDropdown, BDropdownItem } from 'bootstrap-vue-next'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { signout } from '@/modules/miiaFw/utils/miiaFwCognito'
import { useCommonModalStore } from '@/stores/commonModalStore'
import { useMiiaFwStore } from '@/stores/miiaFwStore'
import { useStoreUtils } from '@/stores/util/useStoreUtils'
import { useNetworkStore } from '@/stores/networkStore'
import { onMounted, onUnmounted, ref } from 'vue'
import HeaderModalHandler from '@/components/header/HeaderModalHandler.vue'
import { useDisasterStore } from '@/stores/disasterStore'
import { MODAL_RESULT } from '@/modules/miiaFw/types/components/commonModal'
import { ROUTER_PATHS } from '@/constants/router/paths'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { useZipHandler } from '@/modules/zip/useZipHandler'

const router = useRouter()
const commonModalStore = useCommonModalStore()
const miiaFwStore = useMiiaFwStore()
const networkStore = useNetworkStore()
const disasterStore = useDisasterStore()
const { isAuthorized } = storeToRefs(miiaFwStore)
const storeUtils = useStoreUtils()
const zipHandler = useZipHandler()

const headerModalHandlerRef = ref<InstanceType<typeof HeaderModalHandler> | null>(null)
const disabledStyle = ref({ color: 'gray' })
const shouldDownloadIntervalId = ref<NodeJS.Timer | undefined>()
const shouldDownload = ref(false)

/**
 * @callback openSignout
 * @description サインアウト用のモーダル画面を開く
 * @param { } - 引数なし
 * @return { } 戻り値なし
 */
const openSignoutConfirm = async (): Promise<void> => {
  try {
    const result = await commonModalStore.openConfirmModal({
      body: 'サインアウトします。\nよろしいですか？'
    })
    // オフラインの場合は追加で注意メッセージを表示する（一度サインアウトするとオンラインに復帰するまでサインインできないため）
    if (result === MODAL_RESULT.OK && !networkStore.getIsOnline) {
      await commonModalStore.openConfirmModal({
        title: '注意',
        body: 'オンライン状態に復帰するまでサインインできなくなります。\n本当によろしいですか？'
      })
    }
    await signout()
    if (router.currentRoute.value.path !== ROUTER_PATHS.login)
      router.push({ path: ROUTER_PATHS.login }) // 同じページに遷移させるとNavigation Errorになる。
    storeUtils.allReset()
  } catch {
    //キャンセル押下時は何もしない
  }
}

const openMapDownloadModal = () => {
  if (headerModalHandlerRef.value) {
    headerModalHandlerRef.value.openMapDownloadModal()
  }
}

onMounted(() => {
  // zipダウンロードをすべきかの監視処理を開始する
  startCheckShouldZipDownload()
})

onUnmounted(() => {
  // zipダウンロードをすべきかの監視処理を停止する
  clearInterval(shouldDownloadIntervalId.value as NodeJS.Timeout)
})

/**
 * ダウンロードすべきデータがあるかチェック
 */
const startCheckShouldZipDownload = async () => {
  shouldDownloadIntervalId.value = setInterval(async () => {
    // ダウンロードするデータを取得
    shouldDownload.value = await zipHandler.checkShouldZipDownload()
  }, 5 * 1000)
}
</script>

<template>
  <BDropdown variant="link" no-caret v-if="isAuthorized">
    <HeaderModalHandler ref="headerModalHandlerRef" />
    <template #button-content>
      <span class="hamburger">
        <font-awesome-icon :icon="['fas', 'bars']" />
      </span>
    </template>
    <BDropdownItem @click="zipHandler.downloadZip" class="fs-5" :disabled="!shouldDownload"
      ><font-awesome-icon class="me-1" :icon="['fas', 'file-arrow-down']" />
      ZIPダウンロード</BDropdownItem
    >
    <BDropdownItem @click="zipHandler.uploadZip" class="fs-5" :disabled="!networkStore.getIsOnline"
      ><font-awesome-icon class="me-1" :icon="['fas', 'file-arrow-up']" />
      ZIPアップロード</BDropdownItem
    >
    <BDropdownItem
      @click="openMapDownloadModal"
      class="fs-5"
      :class="!networkStore.isOnline || disasterStore.disaster == null ? 'disabled-menu' : ''"
      :style="!networkStore.isOnline ? disabledStyle : ''"
    >
      <font-awesome-icon icon="map" class="me-1" style="vertical-align: middle" />
      オフライン地図の設定
    </BDropdownItem>
    <BDropdownItem @click="openSignoutConfirm" class="fs-5"
      ><font-awesome-icon class="me-1" :icon="['fas', 'sign-out-alt']" />
      サインアウト</BDropdownItem
    >
  </BDropdown>
</template>

<style lang="scss" scoped>
.hamburger {
  color: $secondary;
  font-size: 3rem;
}

.disabled-menu {
  pointer-events: none;
}

:deep(.dropdown-menu) {
  z-index: 2000 !important; // ドロップダウンメニューを最前面にする。
}
</style>
